/**
 * FormulaDataSource — a `dataSource` whose cells are recomputed from
 * a Lisp expression (scalar) or a projection + transforms (series /
 * tensor). Dispatched by wire type `"formula"` at hydrate time; the
 * underlying `SourceDescriptor` looks identical to a user-backed row
 * of the same shape — a formula can emit any dtype (number, series,
 * Event-shaped obj), so the descriptor alone carries no "this is
 * formula-derived" signal.
 *
 * Peer of `DataSource` rather than a subclass because its write
 * surface differs: the formula engine owns the cells, so `setCell` /
 * `clear` reject before writing (a user write would be clobbered
 * on the next recompute).
 *
 * The `expression` is mutable per-row state and lives on
 * `RowMetadataStore.formula` — not on the descriptor (descriptors
 * are immutable).
 */

import type { Option } from "../../core/option.ts";
import { sheetInternals, sheetOf, sheetStores } from "../../sheet/sheet-stores.ts";
import type { NaiveDate } from "@tento-chrono";

import type { CellValue } from "../../core/dtype.ts";
import { FormulaEngine } from "../../formula/formula-engine.ts";
import { RowWrites } from "../../ledger/row-writes.ts";
import { RowMetadataStore } from "../../stores/row-metadata-store.ts";
import type { ModelRowOptions } from "../row-options.ts";
import type { ModelRow } from "../model-row.ts";
import { declareEngineOwnedCells, peekSourceCell, SourceRow } from "./source-row.ts";
import { declareRowLoader, type RowLoadContext } from "../row-load.ts";
import { Internals } from "../../core/internals.ts";

/** `FormulaDataSource`'s friend record (`Internals`): its constructor registers each instance. */
type FormulaDataSourceInternals = {
  readonly writer: (row: FormulaDataSource) => FormulaDataSource.Writer;
};
const formulaSourceInternals = new Internals<FormulaDataSource, FormulaDataSourceInternals>("FormulaDataSource");

/**
 * **The writer of a formula row** — its expression change
 * (`FormulaDataSource.Writer`). Not on the row: a row a read hands out
 * (`handle.model`, `row.variant`, `RowKind.of`) — and the live class
 * `liveRowOf` reaches — carries no writer. The package's own ops and row
 * options write through it, and this repository's tests through
 * `@tento-lona/sheets/internal-testing`; an app and the SDK's own front-ends
 * change a formula through the SDK row's `update({ formula }, actor)`.
 */
export function formulaWriterOf(row: FormulaDataSource): FormulaDataSource.Writer {
  return formulaSourceInternals.of(row).writer(row);
}

/** A source row whose values a formula computes from other rows. */
export class FormulaDataSource extends SourceRow {
  static readonly #internals: FormulaDataSourceInternals = {
    writer: (row) => row.#writerFace(),
  };

  /** A formula row (`SourceRow`'s arguments); registers its friend record (`formulaWriterOf`). */
  constructor(...args: ConstructorParameters<typeof SourceRow>) {
    super(...args);
    formulaSourceInternals.register(this, FormulaDataSource.#internals);
  }

  static {
    declareEngineOwnedCells(FormulaDataSource, (row, method) => {
      const name = row.label ?? row.id.rawId;
      const verb = method === "write" ? "writes are" : "clears are";
      return `FormulaDataSource.${method}: row "${name}" is a formula — ` +
        `${verb} rejected because the formula engine owns its cells.`;
    });
    declareRowLoader(FormulaDataSource, {
      value: (row, cx) => row.#loadValue(cx),
      children: null,
    });
  }

  /** This row's writer face (`formulaWriterOf`), built on first use. */
  #writer: Option<FormulaDataSource.Writer> = null;

  #writerFace(): FormulaDataSource.Writer {
    if (this.#writer != null) return this.#writer;
    const writer: FormulaDataSource.Writer = Object.freeze({
      row: this,
      setExpression: (formula: string) => this.#setExpression(formula),
    });
    this.#writer = writer;
    return writer;
  }

  /** The formula's Lisp expression (or projection source). */
  get expression(): string {
    return sheetStores(sheetOf(this)).rowMetadata.get(this.id).formula ?? "";
  }

  override optionContributions(): readonly ModelRowOptions.Contribution[] {
    return [
      {
        key: "formula:expression",
        label: "Formula",
        tier: "primary",
        priority: 80,
        section: null,
        target: {
          kind: "source-metadata",
          rowId: this.id,
          path: ["formula"],
        },
        control: { kind: "code", language: "lisp" },
        reRender: "cells",
      },
    ];
  }

  /**
   * Formula cells are owned by the engine — `recomputeDependents`
   * rewrites them from upstream values on every change. A user /
   * facade write would be clobbered on the next recompute, so we
   * reject before the write even lands. Throwing surfaces the
   * no-op loudly (typically a facade mis-wired to a formula row).
   */
  /** The engine owns formula cells — never user-writable. */
  override get cellsWritable(): boolean {
    return false;
  }

  /**
   * Load a formula row's value (its `declareRowLoader` entry). Formula cells
   * are engine-owned, so this loads the VALUES of the rows it depends on —
   * never their trees: a formula under a row it depends on would otherwise
   * load parent → formula → parent → … — and then evaluates this row for the
   * pass's range. No fetch of this row's own server cells: they are empty by
   * design. A row in a formula cycle is not evaluated
   * (`FormulaEngine.cyclicRows`).
   */
  async #loadValue(cx: RowLoadContext): Promise<void> {
    const dependencies: ModelRow[] = [];
    for (const id of sheetInternals(sheetOf(this)).graph.getDependencies(this.id)) {
      const row = sheetOf(this).peekRow(id);
      if (row != null && row !== this) {
        dependencies.push(row);
      }
    }
    await Promise.all(dependencies.map((row) => cx.value(row)));
    FormulaEngine.recomputeRowAndDependents(sheetOf(this), this.id, cx.range);
  }


  /**
   * Update the expression, rebind the row's dependency edges, and recompute
   * this formula's cells (and any downstream formulas) across the sheet's
   * observed range, then persist it. Emits `RowDataChanged(rowId, "formula")`
   * plus one `ColumnDataChanged` per recomputed cell.
   *
   * Rejects (throws) if the new expression would introduce a cycle in the
   * dependency graph — self-references and chains that loop back on
   * themselves are caught before any state mutates.
   *
   * The write enters the client's row-field ledger (`RowWrites`): shown at
   * once in every copy of the row (this sheet's metadata — rebound and
   * recomputed with its dependents — and the SDK's row), announced as
   * `RowMetadataStore.ACTOR`, then sent through the sheet's backend. A
   * refusal takes back only its own write (the projection of the writes that
   * remain, recomputed) and rejects with `RowWriteError` (step `"update"`);
   * a recompute that throws takes the write back before anything is sent.
   */
  async #setExpression(formula: string): Promise<void> {
    const internals = sheetInternals(sheetOf(this));
    if (formula === this.expression && !internals.rowWrites.inFlight(this.id, "formula")) {
      this.#recomputeExpression();
      return;
    }
    if (FormulaEngine.wouldIntroduceCycle(sheetOf(this), this.id, formula)) {
      throw new Error(
        `FormulaDataSource.setExpression: expression "${formula}" on row ` +
          `"${this.label ?? this.id.rawId}" would introduce a ` +
          `dependency cycle.`,
      );
    }
    const write = internals.rowWrites.begin(this.id, RowWrites.set({ formula }), RowMetadataStore.ACTOR, {
      correction: RowMetadataStore.ACTOR,
      quietAttributes: false,
      capture: null,
    });
    // An ephemeral sheet sends nothing: the formula round-trips through
    // serialize → hydrate when the sheet is promoted.
    if (sheetOf(this).ephemeral) {
      internals.rowWrites.accept(write);
      return;
    }
    await internals.rowWrites.run(
      write,
      internals.rowTransport(this.id),
      `the formula of ${this.label ?? this.id.rawId} was not saved`,
    );
  }

  /** Read the computed scalar value for a given date. `null` if not evaluated. */
  peekValue(date: NaiveDate.Partial): CellValue {
    return peekSourceCell(this, date, null) ?? null;
  }

  override toDebug(): Record<string, unknown> {
    const expr = this.expression;
    return {
      ...super.toDebug(),
      ...(expr !== "" ? { expression: expr } : {}),
    };
  }

  #recomputeExpression(): void {
    FormulaEngine.rebindFormulaDataSource(sheetOf(this), this.id);
    // L3 (dark): re-declare this formula's edges onto the ledger after the
    // old-graph rebind. Reached via the store field to avoid value-importing
    // `ledger-shadow` (TDZ through `source-row`; see that module's header).
    sheetStores(sheetOf(this)).sourceCellData.ledgerShadow?.declareFromGraph(
      sheetInternals(sheetOf(this)).graph,
      this,
    );
    const range = FormulaEngine.observedRange(sheetOf(this));
    if (range) {
      FormulaEngine.recomputeRowAndDependents(sheetOf(this), this.id, range);
    }
  }
}

/** Types that go with `FormulaDataSource`: `Writer`. */
export namespace FormulaDataSource {
  /**
   * A formula row's writer, reached through `formulaWriterOf(row)` only (see
   * there). `setExpression` paints (patch + recompute) at once through the
   * row-field ledger; a refusal takes back only its own write before
   * rejecting with `RowWriteError`.
   */
  export interface Writer {
    /** The row this writer writes. */
    readonly row: FormulaDataSource;
    /** Change the expression: cycle-checked, recomputed, persisted; this write taken back on a refusal. */
    setExpression(formula: string): Promise<void>;
  }
}
